import React, { useCallback, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';

import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';

import NestedQMenu from 'components/NestedQMenu';

import * as optionHooks from './options';
import useTxnActionProps from './useTxnActionProps';

export enum TxnMenuOptionEnum {
  EDIT = 'EDIT',
  INVOICE = 'INVOICE',
  LINK_TO_TRANSACTION = 'LINK_TO_TRANSACTION',
  PAYMENT = 'PAYMENT',
  BILL_PRESENTMENT = 'BILL_PRESENTMENT',
  EXCLUDE_REPORTS = 'EXCLUDE_REPORTS',
  EXCLUDE_SPENDING_PLAN = 'EXCLUDE_SPENDING_PLAN',
  RECURRING = 'RECURRING',
  GOAL = 'GOAL',
  VIEW_SERIES = 'VIEW_SERIES',
  DELETE = 'DELETE',
}

interface TxnActionsMenuProps {
  anchorEl: HTMLElement | null;
  context: string;
  onMenuClose?: () => void;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  txn: TransactionRecord;
  txnActionCallback?: (resoures: any[]) => void;
  className?: string;
  iconClassName?: string;
  showOptionsOrder?: TxnMenuOptionEnum[];
}

// This component is a menu that displays available actions for a transaction based on the transaction type
// and other attributes. It is generally used in a 3-dot menu button for a given transaction.
//
// Use TxnActionsMenuButton to render the button that opens this menu.
//
export const TxnActionsMenu = (props: TxnActionsMenuProps) => {
  const {
    anchorEl,
    context = 'txns-actions-menu',
    onMenuClose,
    setAnchorEl,
    txn,
    txnActionCallback,
    showOptionsOrder,
  } = props;

  const txnActionProps = useTxnActionProps()(txn);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    onMenuClose?.();
  }, [onMenuClose, setAnchorEl]);

  const optionsIncludesExclusionItems = useCallback((optionOrder: TxnMenuOptionEnum[]) => {
    return (
      optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_REPORTS) ||
      optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN)
    );
  }, []);

  let options: any[] = [];

  const defaultOrder: TxnMenuOptionEnum[] = [
    TxnMenuOptionEnum.EDIT,
    TxnMenuOptionEnum.INVOICE,
    TxnMenuOptionEnum.LINK_TO_TRANSACTION,
    TxnMenuOptionEnum.PAYMENT,
    TxnMenuOptionEnum.BILL_PRESENTMENT,
    TxnMenuOptionEnum.EXCLUDE_REPORTS,
    TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN,
    TxnMenuOptionEnum.RECURRING,
    TxnMenuOptionEnum.GOAL,
    TxnMenuOptionEnum.VIEW_SERIES,
    TxnMenuOptionEnum.DELETE,
  ];

  const optionOrder = showOptionsOrder ?? defaultOrder;

  const excludeOptions = optionHooks.useExcludeOptions({
    closeMenu,
    context,
    txnActionCallback,
    txnActionProps,
    hideExcludeReports: !optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_REPORTS),
    hideExcludeSpendingPlan: !optionOrder.includes(TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN),
  });

  let exclusionsInList = false;
  optionOrder.forEach((optionType) => {
    switch (optionType) {
      case TxnMenuOptionEnum.EDIT:
        options = options.concat(optionHooks.useEditOptions({ closeMenu, txnActionCallback, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.LINK_TO_TRANSACTION:
        options = options.concat(optionHooks.useLinkToTransactionOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.INVOICE:
        options = options.concat(
          optionHooks.useInvoiceOptions(
            { closeMenu, context, txnActionProps },
            () => txnActionProps.txnToEdit.type === 'INVOICE',
          ),
        );
        break;
      case TxnMenuOptionEnum.PAYMENT:
        options = options.concat(optionHooks.usePaymentOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.BILL_PRESENTMENT:
        options = options.concat(optionHooks.useBillPresentmentOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.EXCLUDE_REPORTS:
        if (!exclusionsInList && optionsIncludesExclusionItems(optionOrder)) {
          options = options.concat(excludeOptions);
        }
        exclusionsInList = true; // Item will not appear twice
        break;
      case TxnMenuOptionEnum.EXCLUDE_SPENDING_PLAN:
        if (!exclusionsInList && optionsIncludesExclusionItems(optionOrder)) {
          options = options.concat(excludeOptions);
        }
        exclusionsInList = true; // Item will not appear twice
        break;
      case TxnMenuOptionEnum.RECURRING:
        options = options.concat(
          optionHooks.useRecurringOptions({ closeMenu, context, txnActionCallback, txnActionProps }),
        );
        break;
      case TxnMenuOptionEnum.GOAL:
        options = options.concat(optionHooks.useGoalOptions({ closeMenu, context, txnActionCallback, txnActionProps }));
        break;
      case TxnMenuOptionEnum.VIEW_SERIES:
        options = options.concat(optionHooks.useViewSeriesOptions({ closeMenu, context, txnActionProps }));
        break;
      case TxnMenuOptionEnum.DELETE:
        options = options.concat(optionHooks.useDeleteOptions({ closeMenu, context, txnActionProps }));
        break;
      default:
        break;
    }
  });

  // remove the last divider if one is set
  if (options.length > 0) {
    options[options.length - 1].divider = false;
  }

  return (
    <NestedQMenu
      id='txn-actions-menu'
      anchorEl={anchorEl}
      onClose={() => {
        setAnchorEl(null);
      }}
      open={Boolean(anchorEl)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      options={options}
    />
  );
};

// This component renders a 3-dot menu button that open the TxnActionsMenu.
//
export const TxnActionsMenuButton = (props: Omit<TxnActionsMenuProps, 'anchorEl' | 'setAnchorEl'>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-label='actions'
        id='txn-actions-button'
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        // style={{ opacity: active ? 1 : 0, padding: 1 }}
        className={props.className}
      >
        <MoreVertIcon className={props.iconClassName} />
      </IconButton>
      {anchorEl && <TxnActionsMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} {...props} />}
    </>
  );
};
